// Shared, non-component values split out of practice-diagram.tsx purely to
// satisfy react-refresh/only-export-components -- a component file can only
// export components.

import { readGraphDiagram, type GraphDiagramSpec } from './practice-graph-data';

export type LineGraphPoint = { label: string; value: number };
export type BarItem = { label: string; value: number; unit: string };

export type PracticeDiagramSpec =
  | { type: 'rectangle'; widthLabel: string; heightLabel: string }
  | {
      type: 'box';
      lengthLabel: string;
      widthLabel: string;
      heightLabel: string;
    }
  | {
      type: 'line_graph';
      xLabel: string;
      yLabel: string;
      points: LineGraphPoint[];
    }
  | { type: 'bars'; bars: BarItem[] }
  | GraphDiagramSpec;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function readLineGraphPoints(value: unknown): LineGraphPoint[] | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const points: LineGraphPoint[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) return null;
    const record = entry as Record<string, unknown>;
    if (!isNonEmptyString(record.label) || typeof record.value !== 'number') {
      return null;
    }
    points.push({ label: record.label, value: record.value });
  }
  return points;
}

function readBarItems(value: unknown): BarItem[] | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const bars: BarItem[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) return null;
    const record = entry as Record<string, unknown>;
    if (
      !isNonEmptyString(record.label) ||
      typeof record.value !== 'number' ||
      !isNonEmptyString(record.unit)
    ) {
      return null;
    }
    bars.push({ label: record.label, value: record.value, unit: record.unit });
  }
  return bars;
}

export function readPracticeDiagram(
  content: unknown,
): PracticeDiagramSpec | null {
  if (typeof content !== 'object' || content === null) return null;
  const diagram = (content as Record<string, unknown>).diagram;
  if (typeof diagram !== 'object' || diagram === null) return null;
  const record = diagram as Record<string, unknown>;
  if (
    record.type === 'rectangle' &&
    isNonEmptyString(record.widthLabel) &&
    isNonEmptyString(record.heightLabel)
  ) {
    return {
      type: 'rectangle',
      widthLabel: record.widthLabel,
      heightLabel: record.heightLabel,
    };
  }
  if (
    record.type === 'box' &&
    isNonEmptyString(record.lengthLabel) &&
    isNonEmptyString(record.widthLabel) &&
    isNonEmptyString(record.heightLabel)
  ) {
    return {
      type: 'box',
      lengthLabel: record.lengthLabel,
      widthLabel: record.widthLabel,
      heightLabel: record.heightLabel,
    };
  }
  if (
    record.type === 'line_graph' &&
    isNonEmptyString(record.xLabel) &&
    isNonEmptyString(record.yLabel)
  ) {
    const points = readLineGraphPoints(record.points);
    if (points !== null) {
      return {
        type: 'line_graph',
        xLabel: record.xLabel,
        yLabel: record.yLabel,
        points,
      };
    }
  }
  if (record.type === 'bars') {
    const bars = readBarItems(record.bars);
    if (bars !== null) {
      return { type: 'bars', bars };
    }
  }
  return readGraphDiagram(record);
}
